import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  InputAdornment,
  Slider,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Snackbar,
  SelectChangeEvent,
  TableSortLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  FormGroup,
  Autocomplete,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Clear as ClearIcon,
  ContentCopy as ContentCopyIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Save as SaveIcon,
  AutoAwesome as AutoAwesomeIcon,
  TrendingUp as TrendingUpIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { keywordService, KeywordIdea } from '../../services/keywordService';
import { websiteService } from '../../services/websiteService';
import { urlService } from '../../services/urlService';
import { supabase } from '../../services/supabase';
import { Website, Url } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`keyword-tabpanel-${index}`}
      aria-labelledby={`keyword-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Extended interface to include country information
interface ExtendedKeywordIdea extends KeywordIdea {
  country?: string;
  countryCode?: string;
}

// New interfaces for related keywords analysis
interface RelatedKeywordData {
  keyword: string;
  currentPosition?: number;
  searchVolume: number;
  competition: string;
  opportunityScore: number;
  semanticSimilarity: number;
  urlId?: string;
  clicks?: number;
  impressions?: number;
  ctr?: number;
}

interface PAAQuestionData {
  question: string;
  hasContent: boolean;
  competitorUrl?: string;
  answerSnippet?: string;
  contentGap: boolean;
}

interface OpportunityData {
  keyword: string;
  type: 'related' | 'paa';
  score: number;
  reason: string;
  action: string;
}

type Order = 'asc' | 'desc';

interface CountryPreset {
  name: string;
  countries: string[];
  languages?: { [key: string]: string };
}


const KeywordResearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ExtendedKeywordIdea[]>([]);
  const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '' });
  
  // Analysis mode state
  const [analysisMode, setAnalysisMode] = useState<'research' | 'analysis'>('research');
  const [baseKeywordForAnalysis, setBaseKeywordForAnalysis] = useState('');
  const [selectedWebsiteForRankings, setSelectedWebsiteForRankings] = useState('');
  const [relatedKeywordsData, setRelatedKeywordsData] = useState<RelatedKeywordData[]>([]);
  const [paaQuestionsData, setPaaQuestionsData] = useState<PAAQuestionData[]>([]);
  const [opportunitiesData, setOpportunitiesData] = useState<OpportunityData[]>([]);
  const [analysisResultsTab, setAnalysisResultsTab] = useState(0);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  
  // Keyword tracking states
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [savingKeywords, setSavingKeywords] = useState(false);
  
  // Website and URL selection states for saving keywords
  const [availableWebsites, setAvailableWebsites] = useState<Website[]>([]);
  const [selectedTargetWebsite, setSelectedTargetWebsite] = useState<Website | null>(null);
  const [availableUrls, setAvailableUrls] = useState<Url[]>([]);
  const [selectedTargetUrl, setSelectedTargetUrl] = useState<Url | null>(null);
  const [loadingWebsites, setLoadingWebsites] = useState(false);
  const [loadingUrls, setLoadingUrls] = useState(false);
  
  // Form states
  const [singleKeyword, setSingleKeyword] = useState('');
  const [bulkKeywords, setBulkKeywords] = useState('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>(['GB']);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [multiCountryMode, setMultiCountryMode] = useState(false);
  
  // Filter states
  const [minSearchVolume, setMinSearchVolume] = useState(0);
  const [competitionFilter, setCompetitionFilter] = useState<string[]>(['LOW', 'MEDIUM', 'HIGH']);
  const [showOnlyQuestions, setShowOnlyQuestions] = useState(false);
  
  // Sorting states
  const [orderBy, setOrderBy] = useState<keyof ExtendedKeywordIdea>('avgMonthlySearches');
  const [order, setOrder] = useState<Order>('desc');
  
  // Custom style for always visible sort arrows
  const sortLabelStyle = {
    '& .MuiTableSortLabel-icon': {
      opacity: 1, // Always show the arrow
    },
  };
  
  // TEMPORARILY DISABLED - Authentication check commented out for testing
  // useEffect(() => {
  //   const checkAuth = async () => {
  //     const { data: { session } } = await supabase.auth.getSession();
  //     if (!session) {
  //       navigate('/login');
  //     }
  //   };
  //   checkAuth();
  // }, [navigate]);

  // Load available websites when keywords are selected OR when in analysis mode
  useEffect(() => {
    if ((selectedKeywords.size > 0 || analysisMode === 'analysis') && availableWebsites.length === 0 && !loadingWebsites) {
      loadWebsites();
    }
  }, [selectedKeywords.size, analysisMode]);

  // Load URLs when a target website is selected
  useEffect(() => {
    if (selectedTargetWebsite) {
      loadUrlsForWebsite(selectedTargetWebsite.website_id);
    } else {
      setAvailableUrls([]);
      setSelectedTargetUrl(null);
    }
  }, [selectedTargetWebsite]);

  const countries = [
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴' },
    { code: 'KR', name: 'Korea', flag: '🇰🇷' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
    { code: 'RU', name: 'Russia', flag: '🇷🇺' },
    { code: 'CN', name: 'China', flag: '🇨🇳' }
  ];

  const countryPresets: CountryPreset[] = [
    {
      name: 'Spanish-speaking Americas',
      countries: ['MX', 'AR', 'CL', 'US'],
      languages: { 'MX': 'es', 'AR': 'es', 'CL': 'es', 'US': 'es' }
    },
    {
      name: 'English-speaking',
      countries: ['US', 'GB', 'CA', 'AU'],
      languages: { 'US': 'en', 'GB': 'en', 'CA': 'en', 'AU': 'en' }
    },
    {
      name: 'Nordic',
      countries: ['SE', 'FI', 'NO', 'DK'],
      languages: { 'SE': 'sv', 'FI': 'fi', 'NO': 'no', 'DK': 'da' }
    }
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'nl', name: 'Dutch' },
    { code: 'sv', name: 'Swedish' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'fi', name: 'Finnish' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'da', name: 'Danish' },
    { code: 'no', name: 'Norwegian' },
    { code: 'ru', name: 'Russian' },
    { code: 'zh', name: 'Chinese (Simplified)' },
    { code: 'zh-TW', name: 'Chinese (Traditional)' }
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  const loadWebsites = async () => {
    setLoadingWebsites(true);
    try {
      const { websites, error } = await websiteService.getWebsites();
      if (error) {
        console.error('Error loading websites:', error);
        setError(`Failed to load websites: ${error}`);
      } else {
        setAvailableWebsites(websites);
      }
    } catch (err) {
      console.error('Error loading websites:', err);
      setError('Failed to load websites');
    } finally {
      setLoadingWebsites(false);
    }
  };

  const loadUrlsForWebsite = async (websiteId: string) => {
    setLoadingUrls(true);
    setAvailableUrls([]);
    setSelectedTargetUrl(null);
    
    try {
      console.log('Loading URLs for website:', websiteId);
      const { urls, error } = await urlService.getUrlsByWebsite(websiteId);
      if (error) {
        console.error('Error loading URLs:', error);
      } else {
        console.log('Loaded URLs:', urls.length);
        setAvailableUrls(urls);
      }
    } catch (err) {
      console.error('Error loading URLs:', err);
    } finally {
      setLoadingUrls(false);
    }
  };

  // Helper function to format website display
  const formatWebsiteDisplay = (baseUrl: string): string => {
    return baseUrl.replace(/^https?:\/\/(www\.)?/, '');
  };

  const handleCountryChange = (event: SelectChangeEvent<typeof selectedCountries>) => {
    const value = event.target.value;
    setSelectedCountries(typeof value === 'string' ? value.split(',') : value);
  };

  const handlePresetClick = (preset: CountryPreset) => {
    setSelectedCountries(preset.countries);
    setMultiCountryMode(true);
    // Set language to the first country's language if available
    if (preset.languages && preset.countries.length > 0) {
      const firstCountryLang = preset.languages[preset.countries[0]];
      if (firstCountryLang) {
        setSelectedLanguage(firstCountryLang);
      }
    }
  };

  const handleSingleSearch = async () => {
    if (!singleKeyword.trim()) {
      setError('Please enter a keyword to research');
      return;
    }

    await performKeywordResearch([singleKeyword.trim()]);
  };

  const handleBulkSearch = async () => {
    const keywords = bulkKeywords
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    if (keywords.length === 0) {
      setError('Please enter at least one keyword');
      return;
    }

    if (keywords.length > 100) {
      setError('Please enter no more than 100 keywords at once');
      return;
    }

    await performKeywordResearch(keywords);
  };

  const performKeywordResearch = async (keywords: string[]) => {
    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const allResults: ExtendedKeywordIdea[] = [];
      
      // If multi-country mode, search each country separately
      const countriesToSearch = multiCountryMode ? selectedCountries : [selectedCountries[0]];
      
      for (const countryCode of countriesToSearch) {
        const { keywords: keywordResults, error: apiError } = await keywordService.getKeywordIdeas(
          keywords,
          countryCode,
          selectedLanguage
        );

        if (apiError) {
          setError(apiError);
          continue;
        }

        // Add country information to each result
        const country = countries.find(c => c.code === countryCode);
        const extendedResults = keywordResults.map(result => ({
          ...result,
          country: country?.name || countryCode,
          countryCode: countryCode
        }));

        allResults.push(...extendedResults);
      }

      setResults(allResults);
      
      // Save to history if we have results
      if (allResults.length > 0) {
        // Get the first available website for history tracking
        try {
          const { websites } = await websiteService.getWebsites();
          if (websites.length > 0) {
            const websiteId = websites[0].website_id || websites[0].id?.toString();
            if (websiteId) {
              keywordService.saveKeywordResearch(
                websiteId,
                allResults,
                tabValue === 0 ? 'single' : 'bulk',
                keywords
              ).catch(console.error); // Don't block on save
            }
          }
        } catch (historyError) {
          // Silently fail history save - not critical
          console.error('Failed to save keyword research history:', historyError);
        }
      }
    } catch (err) {
      setError('Failed to connect to keyword service. Please ensure the backend is running.');
      console.error('Keyword research error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property: keyof ExtendedKeywordIdea) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const descendingComparator = <T,>(a: T, b: T, orderBy: keyof T) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (
    order: Order,
    orderBy: keyof ExtendedKeywordIdea,
  ): (a: ExtendedKeywordIdea, b: ExtendedKeywordIdea) => number => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const filteredResults = results.filter(result => {
    if (result.avgMonthlySearches < minSearchVolume) return false;
    if (!competitionFilter.includes(result.competition)) return false;
    if (showOnlyQuestions) {
      const questionWords = ['what', 'when', 'where', 'why', 'how', 'which', 'who', 'can', 'does', 'is', 'are', 'will'];
      const lowerKeyword = result.keyword.toLowerCase();
      const hasQuestion = questionWords.some(word => 
        lowerKeyword.includes(word + ' ') || lowerKeyword.includes(' ' + word) || lowerKeyword.startsWith(word + ' ')
      );
      if (!hasQuestion) return false;
    }
    return true;
  });

  // Sort the filtered results
  const sortedResults = React.useMemo(
    () => [...filteredResults].sort(getComparator(order, orderBy)),
    [filteredResults, order, orderBy]
  );

  const exportToCSV = () => {
    const headers = multiCountryMode 
      ? ['Keyword', 'Country', 'Avg Monthly Searches', 'Competition', 'Low Bid', 'High Bid']
      : ['Keyword', 'Avg Monthly Searches', 'Competition', 'Low Bid', 'High Bid'];
    
    const rows = sortedResults.map(r => {
      const baseRow = [
        r.keyword,
        r.avgMonthlySearches,
        r.competition,
        r.lowTopPageBid.toFixed(2),
        r.highTopPageBid.toFixed(2)
      ];
      
      if (multiCountryMode) {
        return [r.keyword, r.country || r.countryCode || '', ...baseRow.slice(1)];
      }
      return baseRow;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keyword-research-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearResults = () => {
    setResults([]);
    setSingleKeyword('');
    setBulkKeywords('');
    setError(null);
    setSelectedKeywords(new Set());
  };

  const getCompetitionColor = (competition: string) => {
    switch (competition) {
      case 'LOW': return 'success';
      case 'MEDIUM': return 'warning';
      case 'HIGH': return 'error';
      default: return 'default';
    }
  };

  const handleCopyKeyword = (keyword: string) => {
    navigator.clipboard.writeText(keyword).then(() => {
      setCopySnackbar({ open: true, message: `Copied: ${keyword}` });
    }).catch(() => {
      setCopySnackbar({ open: true, message: 'Failed to copy keyword' });
    });
  };
  
  const handleKeywordSelection = (keyword: string) => {
    const newSelected = new Set(selectedKeywords);
    if (newSelected.has(keyword)) {
      newSelected.delete(keyword);
    } else {
      newSelected.add(keyword);
    }
    setSelectedKeywords(newSelected);
  };
  
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allKeywords = new Set(sortedResults.map(r => r.keyword));
      setSelectedKeywords(allKeywords);
    } else {
      setSelectedKeywords(new Set());
    }
  };
  
  // Advanced opportunity scoring algorithm
  const calculateOpportunityScore = (keyword: RelatedKeywordData): number => {
    let score = 0;
    const maxScore = 100;
    
    // 1. Search Volume Impact (0-35 points)
    const volumeScore = (() => {
      if (keyword.searchVolume >= 10000) return 35;
      if (keyword.searchVolume >= 5000) return 30;
      if (keyword.searchVolume >= 1000) return 25;
      if (keyword.searchVolume >= 500) return 20;
      if (keyword.searchVolume >= 100) return 15;
      if (keyword.searchVolume >= 50) return 10;
      return 5;
    })();
    score += volumeScore;
    
    // 2. Position/Ranking Impact (0-40 points)
    const positionScore = (() => {
      if (!keyword.currentPosition) return 40; // Not ranking = massive opportunity
      if (keyword.currentPosition > 50) return 35; // Beyond page 5 = very high opportunity
      if (keyword.currentPosition > 20) return 30; // Page 3-5 = high opportunity  
      if (keyword.currentPosition > 10) return 25; // Page 2 = medium-high opportunity
      if (keyword.currentPosition > 5) return 15; // Page 1 bottom = medium opportunity
      if (keyword.currentPosition > 3) return 8; // Top 5 = small opportunity
      return 3; // Top 3 = minimal opportunity (already doing well)
    })();
    score += positionScore;
    
    // 3. Competition Level Impact (0-15 points)
    const competitionScore = (() => {
      if (keyword.competition === 'LOW') return 15;
      if (keyword.competition === 'MEDIUM') return 10;
      return 5; // HIGH competition
    })();
    score += competitionScore;
    
    // 4. Semantic Similarity Bonus (0-10 points)
    const similarityBonus = Math.floor(keyword.semanticSimilarity / 10);
    score += Math.min(similarityBonus, 10);
    
    // 5. Quick Win Multiplier
    // Keywords with high volume + not ranking = 1.2x multiplier
    if (!keyword.currentPosition && keyword.searchVolume > 1000) {
      score = Math.floor(score * 1.2);
    }
    
    // 6. Low-hanging fruit bonus
    // Keywords ranking 11-20 with decent volume get bonus (easy to move to page 1)
    if (keyword.currentPosition && keyword.currentPosition >= 11 && keyword.currentPosition <= 20 && keyword.searchVolume > 500) {
      score += 5;
    }
    
    return Math.min(score, maxScore);
  };

  // Main analysis function
  const handleAnalyzeKeywordUniverse = async () => {
    if (!baseKeywordForAnalysis.trim() || !selectedWebsiteForRankings) {
      setError('Please enter a keyword and select a website for analysis');
      return;
    }

    setAnalysisLoading(true);
    setError(null);
    
    try {
      const { getSupabaseClient } = await import('../../services/supabase');
      const supabaseAdmin = await getSupabaseClient(true);
      
      // 1. Get related keywords from existing serp_rankings table
      console.log('🔍 Searching for related keywords...');
      const baseKeywordWords = baseKeywordForAnalysis.toLowerCase().split(' ');
      const searchPattern = baseKeywordForAnalysis; // Use full keyword for better matching
      
      const { data: rankingsData, error: rankingsError } = await getSupabaseClient(true)
        .from('serp_rankings')
        .select('keyword, ranking, url_id, check_date')
        .like('url_id', `${selectedWebsiteForRankings}-%`)
        .ilike('keyword', `%${searchPattern}%`)
        .order('check_date', { ascending: false })
        .limit(100);

      if (rankingsError) {
        console.error('Rankings query error:', rankingsError);
        throw new Error(`Failed to fetch rankings: ${rankingsError.message}`);
      }

      console.log(`🔍 Found ${rankingsData?.length || 0} related keywords`);

      // 2. Deduplicate by keyword, keeping latest rankings
      const keywordMap = new Map();
      rankingsData?.forEach(item => {
        if (!keywordMap.has(item.keyword) || 
            new Date(item.check_date) > new Date(keywordMap.get(item.keyword).check_date)) {
          keywordMap.set(item.keyword, item);
        }
      });

      const uniqueKeywords = Array.from(keywordMap.values());
      console.log(`🔍 Deduplicated to ${uniqueKeywords.length} unique keywords`);

      // 3. Get search volume data from Google Ads API (if available)
      let volumeData: ExtendedKeywordIdea[] = [];
      if (uniqueKeywords.length > 0) {
        try {
          const keywordList = uniqueKeywords.map(r => r.keyword).slice(0, 50); // Limit for API
          const { keywords: apiResults } = await keywordService.getKeywordIdeas(
            keywordList,
            selectedCountries[0] || 'GB',
            selectedLanguage
          );
          volumeData = apiResults;
          console.log(`📊 Got volume data for ${volumeData.length} keywords`);
        } catch (apiError) {
          console.warn('Google Ads API failed, using mock data:', apiError);
        }
      }

      // 4. Combine rankings + volume data
      const relatedWithData: RelatedKeywordData[] = uniqueKeywords.map(ranking => {
        const volumeInfo = volumeData.find(v => 
          v.keyword.toLowerCase() === ranking.keyword.toLowerCase()
        );
        
        // Calculate semantic similarity (simple word overlap for now)
        const similarity = calculateSemanticSimilarity(baseKeywordForAnalysis, ranking.keyword);
        
        const keywordData: RelatedKeywordData = {
          keyword: ranking.keyword,
          currentPosition: ranking.ranking,
          searchVolume: volumeInfo?.avgMonthlySearches || generateMockVolume(ranking.keyword),
          competition: volumeInfo?.competition || 'MEDIUM',
          opportunityScore: 0, // Will be calculated below
          semanticSimilarity: similarity,
          urlId: ranking.url_id
        };
        
        keywordData.opportunityScore = calculateOpportunityScore(keywordData);
        return keywordData;
      });

      // Sort by opportunity score
      relatedWithData.sort((a, b) => b.opportunityScore - a.opportunityScore);

      // 5. Get PAA questions from backend API
      let paaData: PAAQuestionData[] = [];
      console.log('🔍 Searching for real PAA questions via API...');
      
      try {
        // Use the actual PAA API endpoint with full keyword
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/paa/questions?website_id=${selectedWebsiteForRankings}&search=${encodeURIComponent(baseKeywordForAnalysis)}&limit=20`);
        
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data.questions.length > 0) {
            console.log(`🔍 Found ${result.data.questions.length} REAL PAA questions from API`);
            
            paaData = result.data.questions.map((paa: any) => ({
              question: paa.question,
              hasContent: Boolean(paa.answer && paa.answer.trim().length > 0),
              competitorUrl: paa.source_url || paa.url,
              answerSnippet: paa.answer || paa.snippet || `Content found for "${paa.question}"`,
              contentGap: !paa.answer || paa.answer.trim().length === 0
            }));
          } else {
            console.log('📝 No PAA questions found in database, using realistic mock data for demo');
            paaData = generateRealisticPAAQuestions(baseKeywordForAnalysis);
          }
        } else {
          console.warn('PAA API not available (using realistic mock data):', response.status);
          paaData = generateRealisticPAAQuestions(baseKeywordForAnalysis);
        }
      } catch (paaFetchError) {
        console.warn('PAA API request failed (using realistic mock data):', paaFetchError);
        paaData = generateRealisticPAAQuestions(baseKeywordForAnalysis);
      }

      // 6. Generate opportunities summary
      const opportunities: OpportunityData[] = [
        ...relatedWithData
          .filter(k => k.opportunityScore > 70)
          .slice(0, 5)
          .map(k => ({
            keyword: k.keyword,
            type: 'related' as const,
            score: k.opportunityScore,
            reason: k.currentPosition ? 
              `Currently ranking #${k.currentPosition}, high opportunity to improve` :
              `Not ranking for ${k.searchVolume.toLocaleString()} monthly searches`,
            action: k.currentPosition ? 'Optimize existing content' : 'Create new content'
          })),
        ...paaData
          .filter(p => p.contentGap)
          .slice(0, 3)
          .map(p => ({
            keyword: p.question,
            type: 'paa' as const,
            score: 85,
            reason: 'Content gap - people are asking this question',
            action: 'Create FAQ or blog post'
          }))
      ];

      setRelatedKeywordsData(relatedWithData);
      setPaaQuestionsData(paaData);
      setOpportunitiesData(opportunities);
      
      console.log(`✅ Analysis complete: ${relatedWithData.length} related keywords, ${paaData.length} PAA questions (${paaData.some(p => p.answerSnippet?.includes('Real PAA data not available')) ? 'realistic mock data - backend API not available' : 'from backend API'})`);

    } catch (err: any) {
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to analyze keyword universe');
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Helper functions
  const calculateSemanticSimilarity = (base: string, target: string): number => {
    const baseWords = base.toLowerCase().split(' ');
    const targetWords = target.toLowerCase().split(' ');
    const commonWords = baseWords.filter(word => targetWords.includes(word));
    return (commonWords.length / Math.max(baseWords.length, targetWords.length)) * 100;
  };

  const generateMockVolume = (keyword: string): number => {
    // Generate realistic mock search volume
    const baseVolume = keyword.length > 25 ? 200 : keyword.includes('how') ? 1500 : 800;
    return baseVolume + Math.floor(Math.random() * 2000);
  };

  const generateMockPAAQuestions = (baseKeyword: string): PAAQuestionData[] => {
    const questionStarters = ['How to', 'What is', 'Why does', 'When should', 'Where can'];
    return questionStarters.map(starter => ({
      question: `${starter} ${baseKeyword}?`,
      hasContent: Math.random() > 0.6,
      contentGap: Math.random() > 0.4,
      answerSnippet: `Learn about ${baseKeyword} and discover...`
    }));
  };

  const generateRealisticPAAQuestions = (baseKeyword: string): PAAQuestionData[] => {
    // Create realistic PAA questions based on the specific keyword
    const words = baseKeyword.toLowerCase().split(' ');
    
    // Generate questions specific to the keyword topic
    let realisticQuestions = [];
    
    if (baseKeyword.includes('tickets')) {
      const entity = words.filter(w => w !== 'tickets').join(' ');
      realisticQuestions = [
        `How much do ${baseKeyword} cost?`,
        `Where is the best place to buy ${baseKeyword}?`,
        `When do ${baseKeyword} go on sale?`,
        `What is the face value of ${baseKeyword}?`,
        `How to get cheap ${baseKeyword}?`,
        `Are ${baseKeyword} transferable?`,
        `What are the best seats for ${entity}?`,
        `Can you resell ${baseKeyword}?`
      ];
    } else if (baseKeyword.includes('hotel') || baseKeyword.includes('hotels')) {
      realisticQuestions = [
        `What are the best ${baseKeyword}?`,
        `How much do ${baseKeyword} cost per night?`,
        `Where to find cheap ${baseKeyword}?`,
        `Which ${baseKeyword} have free parking?`,
        `Are ${baseKeyword} pet friendly?`,
        `What amenities do ${baseKeyword} offer?`,
        `When is the best time to book ${baseKeyword}?`,
        `Do ${baseKeyword} include breakfast?`
      ];
    } else {
      // Generic questions for other keywords
      realisticQuestions = [
        `What is ${baseKeyword}?`,
        `How does ${baseKeyword} work?`,
        `Why is ${baseKeyword} important?`,
        `Where to find ${baseKeyword}?`,
        `When to use ${baseKeyword}?`,
        `What are the benefits of ${baseKeyword}?`,
        `How much does ${baseKeyword} cost?`,
        `Is ${baseKeyword} worth it?`
      ];
    }

    return realisticQuestions.slice(0, 10).map(question => ({
      question,
      hasContent: Math.random() > 0.7,
      contentGap: Math.random() > 0.5,
      competitorUrl: Math.random() > 0.5 ? `example.com/${baseKeyword.replace(/\s+/g, '-')}` : undefined,
      answerSnippet: `Content analysis would show actual answer snippets from search results for "${question}"`
    }));
  };

  // Export functions for analysis results
  const exportAnalysisToCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    
    // Related Keywords CSV
    if (relatedKeywordsData.length > 0) {
      const headers = ['Keyword', 'Search Volume', 'Current Position', 'Competition', 'Opportunity Score', 'Semantic Similarity', 'URL ID'];
      const rows = relatedKeywordsData.map(k => [
        k.keyword,
        k.searchVolume,
        k.currentPosition || 'Not ranking',
        k.competition,
        k.opportunityScore,
        `${k.semanticSimilarity.toFixed(1)}%`,
        k.urlId || ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `keyword-analysis-${baseKeywordForAnalysis.replace(/\s+/g, '-')}-${timestamp}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const exportPAAToCSV = () => {
    if (paaQuestionsData.length === 0) return;
    
    const timestamp = new Date().toISOString().split('T')[0];
    const headers = ['Question', 'Has Content', 'Content Gap', 'Competitor URL', 'Answer Snippet'];
    const rows = paaQuestionsData.map(q => [
      q.question,
      q.hasContent ? 'Yes' : 'No',
      q.contentGap ? 'Yes' : 'No',
      q.competitorUrl || '',
      q.answerSnippet || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paa-questions-${baseKeywordForAnalysis.replace(/\s+/g, '-')}-${timestamp}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSaveSelectedKeywords = async () => {
    if (selectedKeywords.size === 0 || !selectedTargetWebsite || !selectedTargetUrl) {
      setError('Please select keywords, a target website, and a target URL');
      return;
    }
    
    setSavingKeywords(true);
    setError(null);
    
    try {
      console.log('Selected target website:', selectedTargetWebsite);
      console.log('Selected target URL:', selectedTargetUrl);
      
      // Prepare keywords for bulk insert
      const keywordsToInsert = Array.from(selectedKeywords).map(keyword => ({
        keyword: keyword,
        website_id: selectedTargetWebsite.website_id,
        url_id: selectedTargetUrl.url_id, // Use the url_id string
        search_volume: sortedResults.find(r => r.keyword === keyword)?.avgMonthlySearches || null,
        difficulty: null,
        is_primary: false, // Always set to false (secondary keyword)
        last_updated: new Date().toISOString(),
      }));
      
      console.log('Keywords to insert:', keywordsToInsert);
      
      const { count, error } = await keywordService.bulkCreateKeywords(keywordsToInsert);
      
      if (error) {
        setError(`Failed to save keywords: ${error}`);
      } else {
        const successMessage = `Successfully saved ${count} secondary keywords to ${selectedTargetWebsite.base_url} (${selectedTargetUrl.full_url})`;
        setCopySnackbar({ open: true, message: successMessage });
        // Clear selection after successful save
        setSelectedKeywords(new Set());
        // Reset website/URL selection
        setSelectedTargetWebsite(null);
        setSelectedTargetUrl(null);
        setAvailableWebsites([]);
        setAvailableUrls([]);
      }
    } catch (err) {
      setError('Failed to save keywords');
      console.error('Error saving keywords:', err);
    } finally {
      setSavingKeywords(false);
    }
  };

  // Component: Enhanced Related Keywords Table
  const RelatedKeywordsTable: React.FC<{ data: RelatedKeywordData[] }> = ({ data }) => {
    const [sortField, setSortField] = useState<keyof RelatedKeywordData>('opportunityScore');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    const handleSort = (field: keyof RelatedKeywordData) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('desc');
      }
    };

    const sortedData = [...data].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    const getPositionTrend = (position?: number) => {
      if (!position) return null;
      if (position <= 3) return { color: 'success.main', icon: '🥇' };
      if (position <= 10) return { color: 'warning.main', icon: '📊' };
      if (position <= 20) return { color: 'info.main', icon: '📈' };
      return { color: 'error.main', icon: '📉' };
    };

    const getOpportunityLabel = (score: number) => {
      if (score >= 80) return { label: 'HIGH', color: 'success' };
      if (score >= 60) return { label: 'MEDIUM', color: 'warning' };
      return { label: 'LOW', color: 'error' };
    };

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ height: '100%', maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <TableCell sx={{ fontWeight: 700, color: 'white', bgcolor: '#667eea' }}>
                <TableSortLabel
                  active={sortField === 'keyword'}
                  direction={sortField === 'keyword' ? sortDirection : 'desc'}
                  onClick={() => handleSort('keyword')}
                  sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  Related Keyword
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: 'white', bgcolor: '#667eea' }}>
                <TableSortLabel
                  active={sortField === 'searchVolume'}
                  direction={sortField === 'searchVolume' ? sortDirection : 'desc'}
                  onClick={() => handleSort('searchVolume')}
                  sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  Volume
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: 'white', bgcolor: '#667eea' }}>
                <TableSortLabel
                  active={sortField === 'currentPosition'}
                  direction={sortField === 'currentPosition' ? sortDirection : 'desc'}
                  onClick={() => handleSort('currentPosition')}
                  sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  Our Position
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: 'white', bgcolor: '#667eea' }}>
                <TableSortLabel
                  active={sortField === 'competition'}
                  direction={sortField === 'competition' ? sortDirection : 'desc'}
                  onClick={() => handleSort('competition')}
                  sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  Competition
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: 'white', bgcolor: '#667eea' }}>
                <TableSortLabel
                  active={sortField === 'opportunityScore'}
                  direction={sortField === 'opportunityScore' ? sortDirection : 'desc'}
                  onClick={() => handleSort('opportunityScore')}
                  sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  Opportunity
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: 'white', bgcolor: '#667eea' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <SearchIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography variant="h6" color="text.secondary">No related keywords found</Typography>
                    <Typography variant="body2" color="text.disabled">
                      Try a different keyword or check your database for ranking data
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((keyword, index) => {
                const trend = getPositionTrend(keyword.currentPosition);
                const opportunity = getOpportunityLabel(keyword.opportunityScore);
                
                return (
                  <TableRow 
                    key={index} 
                    hover 
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'rgba(103, 126, 234, 0.04)',
                        transform: 'scale(1.001)',
                        transition: 'all 0.2s ease'
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                            {keyword.keyword}
                          </Typography>
                          <Tooltip title="Copy keyword">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyKeyword(keyword.keyword)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={`${keyword.semanticSimilarity.toFixed(0)}% match`}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.7rem',
                              height: 20,
                              color: keyword.semanticSimilarity > 70 ? 'success.main' : 
                                     keyword.semanticSimilarity > 40 ? 'warning.main' : 'error.main',
                              borderColor: keyword.semanticSimilarity > 70 ? 'success.main' : 
                                          keyword.semanticSimilarity > 40 ? 'warning.main' : 'error.main'
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                          {keyword.searchVolume.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          monthly searches
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {keyword.currentPosition ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Chip 
                            label={`#${keyword.currentPosition}`} 
                            size="small"
                            sx={{
                              bgcolor: trend?.color,
                              color: 'white',
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                            {trend?.icon} {keyword.currentPosition <= 10 ? 'Page 1' : `Page ${Math.ceil(keyword.currentPosition / 10)}`}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Chip 
                            label="Not ranking" 
                            size="small" 
                            sx={{ 
                              bgcolor: 'grey.300',
                              color: 'grey.700',
                              fontWeight: 500
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'error.main' }}>
                            🎯 Big opportunity!
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={keyword.competition}
                        size="small"
                        color={getCompetitionColor(keyword.competition) as any}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box 
                            sx={{ 
                              width: 50, 
                              height: 8, 
                              bgcolor: 'grey.200', 
                              borderRadius: 4,
                              overflow: 'hidden',
                              position: 'relative'
                            }}
                          >
                            <Box 
                              sx={{ 
                                width: `${keyword.opportunityScore}%`, 
                                height: '100%',
                                background: keyword.opportunityScore > 80 ? 
                                  'linear-gradient(90deg, #4caf50, #8bc34a)' : 
                                  keyword.opportunityScore > 50 ? 
                                  'linear-gradient(90deg, #ff9800, #ffc107)' : 
                                  'linear-gradient(90deg, #f44336, #ff5722)',
                                transition: 'width 0.5s ease',
                                borderRadius: 4
                              }} 
                            />
                          </Box>
                          <Typography variant="caption" sx={{ minWidth: 35, fontWeight: 600 }}>
                            {keyword.opportunityScore}%
                          </Typography>
                        </Box>
                        <Chip 
                          label={opportunity.label}
                          size="small"
                          color={opportunity.color as any}
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.65rem',
                            height: 18,
                            fontWeight: 600,
                            '& .MuiChip-label': { px: 0.75 }
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Button 
                          size="small" 
                          variant="contained"
                          sx={{ 
                            fontSize: '0.65rem',
                            minWidth: 'auto',
                            px: 1,
                            py: 0.25,
                            bgcolor: '#7b61ff',
                            '&:hover': { bgcolor: '#6b51e0' }
                          }}
                          startIcon={<TrendingUpIcon sx={{ fontSize: '0.8rem !important' }} />}
                        >
                          Track
                        </Button>
                        {!keyword.currentPosition && (
                          <Button 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.6rem',
                              minWidth: 'auto',
                              px: 1,
                              py: 0.25,
                              borderColor: 'success.main',
                              color: 'success.main',
                              '&:hover': { 
                                borderColor: 'success.dark',
                                bgcolor: 'success.50'
                              }
                            }}
                          >
                            Target
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Component: Enhanced PAA Questions Cards
  const PAAQuestionsCards: React.FC<{ data: PAAQuestionData[] }> = ({ data }) => (
    <Box sx={{ height: '100%', maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
      {data.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <QuestionAnswerIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No PAA questions found
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Try a different keyword or check your database for PAA data
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.map((paa, index) => (
            <Card 
              key={index} 
              variant="outlined" 
              sx={{ 
                p: 3,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.8) 100%)',
                border: '1px solid rgba(224, 231, 255, 0.5)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(103, 126, 234, 0.15)',
                  borderColor: 'rgba(103, 126, 234, 0.3)'
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#2c3e50',
                      mb: 1,
                      lineHeight: 1.3
                    }}
                  >
                    {paa.question}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Question #{index + 1} • People Also Ask
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                  <Chip 
                    label={paa.hasContent ? "✅ We have content" : "❌ Content gap"} 
                    size="small"
                    sx={{
                      bgcolor: paa.hasContent ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                      color: paa.hasContent ? '#2e7d32' : '#f57c00',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                  {paa.contentGap && (
                    <Chip 
                      label="🎯 Opportunity" 
                      size="small" 
                      sx={{
                        bgcolor: 'rgba(103, 126, 234, 0.1)',
                        color: '#677eea',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                </Box>
              </Box>
              
              {paa.answerSnippet && (
                <Box sx={{ 
                  bgcolor: 'rgba(248, 250, 252, 0.8)', 
                  borderRadius: 1, 
                  p: 2, 
                  mb: 2,
                  borderLeft: '3px solid #677eea'
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    "{paa.answerSnippet}"
                  </Typography>
                  {paa.competitorUrl && (
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                      Source: {paa.competitorUrl}
                    </Typography>
                  )}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!paa.hasContent ? (
                    <Button 
                      size="small" 
                      variant="contained"
                      sx={{ 
                        bgcolor: 'success.main',
                        '&:hover': { bgcolor: 'success.dark' },
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}
                      startIcon={<AutoAwesomeIcon sx={{ fontSize: '0.8rem !important' }} />}
                    >
                      Create Content
                    </Button>
                  ) : (
                    <Button 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        borderColor: 'info.main',
                        color: 'info.main',
                        fontSize: '0.75rem',
                        '&:hover': { bgcolor: 'info.50' }
                      }}
                      startIcon={<SearchIcon sx={{ fontSize: '0.8rem !important' }} />}
                    >
                      Optimize Content
                    </Button>
                  )}
                  <Button 
                    size="small" 
                    variant="text"
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}
                    startIcon={<QuestionAnswerIcon sx={{ fontSize: '0.8rem !important' }} />}
                  >
                    Research
                  </Button>
                </Box>
                <Typography variant="caption" color="text.disabled">
                  Priority: {paa.contentGap ? 'HIGH' : 'MEDIUM'}
                </Typography>
              </Box>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  // Component: Opportunities Cards
  const OpportunitiesCards: React.FC<{ data: OpportunityData[] }> = ({ data }) => (
    <Box sx={{ height: '100%', maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
      {data.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">No opportunities identified</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.map((opportunity, index) => (
            <Card key={index} variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {opportunity.keyword}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {opportunity.reason}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                    Recommended: {opportunity.action}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                  <Chip 
                    label={opportunity.type === 'related' ? 'Related Keyword' : 'PAA Question'}
                    size="small"
                    color={opportunity.type === 'related' ? 'primary' : 'secondary'}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Score:</Typography>
                    <Chip 
                      label={`${opportunity.score}%`}
                      size="small"
                      color={opportunity.score > 80 ? 'success' : opportunity.score > 60 ? 'warning' : 'default'}
                    />
                  </Box>
                </Box>
              </Box>
              
              <Button variant="outlined" size="small" disabled>
                Take Action
              </Button>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Keyword Research
          </Typography>
          {((analysisMode === 'research' && results.length > 0) || 
            (analysisMode === 'analysis' && relatedKeywordsData.length > 0)) && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {analysisMode === 'research' && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={exportToCSV}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearResults}
                    color="error"
                  >
                    Clear Results
                  </Button>
                </>
              )}
              {analysisMode === 'analysis' && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={exportAnalysisToCSV}
                    disabled={relatedKeywordsData.length === 0}
                    size="small"
                  >
                    Export Keywords
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={exportPAAToCSV}
                    disabled={paaQuestionsData.length === 0}
                    size="small"
                  >
                    Export PAA
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={() => {
                      setRelatedKeywordsData([]);
                      setPaaQuestionsData([]);
                      setOpportunitiesData([]);
                      setBaseKeywordForAnalysis('');
                    }}
                    color="error"
                    size="small"
                  >
                    Clear Analysis
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Mode Toggle */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={analysisMode}
            exclusive
            onChange={(e, newMode) => newMode && setAnalysisMode(newMode)}
            sx={{ 
              bgcolor: 'white', 
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '12px !important',
                px: 3,
                py: 1.5,
                '&.Mui-selected': {
                  bgcolor: '#7b61ff',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#6b51e0'
                  }
                }
              }
            }}
          >
            <ToggleButton value="research">
              <SearchIcon sx={{ mr: 1 }} />
              Keyword Research
            </ToggleButton>
            <ToggleButton value="analysis">
              <AutoAwesomeIcon sx={{ mr: 1 }} />
              Related + Rankings Analysis
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>


        {/* Main Content */}
        {analysisMode === 'research' ? (
          // EXISTING KEYWORD RESEARCH FUNCTIONALITY
          <Grid container spacing={3} sx={{ height: 'calc(100vh - 200px)' }}>
            {/* Input Section - Fixed on the left */}
            <Grid item xs={12} lg={4} sx={{ 
              height: '100%',
              overflowY: 'auto',
              position: 'sticky',
              top: 0
            }}>
            <Card sx={{ height: 'fit-content' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Single Keyword" />
                  <Tab label="Bulk Keywords" />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Enter keyword"
                    value={singleKeyword}
                    onChange={(e) => setSingleKeyword(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !loading) {
                        handleSingleSearch();
                      }
                    }}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={multiCountryMode}
                          onChange={(e) => {
                            setMultiCountryMode(e.target.checked);
                            if (!e.target.checked) {
                              setSelectedCountries([selectedCountries[0] || 'GB']);
                            }
                          }}
                        />
                      }
                      label="Select multiple countries"
                    />
                    
                    {multiCountryMode && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {countryPresets.map((preset) => (
                          <Button
                            key={preset.name}
                            size="small"
                            variant="outlined"
                            onClick={() => handlePresetClick(preset)}
                            disabled={loading}
                          >
                            {preset.name}
                          </Button>
                        ))}
                      </Box>
                    )}
                    
                    <FormControl fullWidth>
                      <InputLabel>Country{multiCountryMode ? 'ies' : ''}</InputLabel>
                      <Select
                        multiple={multiCountryMode}
                        value={multiCountryMode ? selectedCountries : selectedCountries[0]}
                        onChange={(e: SelectChangeEvent<string | string[]>) => {
                          if (multiCountryMode) {
                            const value = e.target.value;
                            setSelectedCountries(typeof value === 'string' ? value.split(',') : value);
                          } else {
                            setSelectedCountries([e.target.value as string]);
                          }
                        }}
                        label={`Country${multiCountryMode ? 'ies' : ''}`}
                        disabled={loading}
                        input={multiCountryMode ? <OutlinedInput label="Countries" /> : undefined}
                        renderValue={multiCountryMode ? (selected) => {
                          const selectedArray = selected as string[];
                          return selectedArray.map(code => {
                            const country = countries.find(c => c.code === code);
                            return country ? `${country.flag} ${country.name}` : code;
                          }).join(', ');
                        } : undefined}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {multiCountryMode && (
                              <Checkbox checked={selectedCountries.indexOf(country.code) > -1} />
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{country.flag}</span>
                              <span>{country.name}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <FormControl fullWidth>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      label="Language"
                      disabled={loading}
                    >
                      {languages.map((language) => (
                        <MenuItem key={language.code} value={language.code}>
                          {language.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSingleSearch}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  >
                    {loading ? 'Searching...' : 'Get Keyword Ideas'}
                  </Button>
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Enter keywords (one per line)"
                    value={bulkKeywords}
                    onChange={(e) => setBulkKeywords(e.target.value)}
                    disabled={loading}
                    helperText="Enter up to 100 keywords, one per line"
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={multiCountryMode}
                          onChange={(e) => {
                            setMultiCountryMode(e.target.checked);
                            if (!e.target.checked) {
                              setSelectedCountries([selectedCountries[0] || 'GB']);
                            }
                          }}
                        />
                      }
                      label="Select multiple countries"
                    />
                    
                    {multiCountryMode && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {countryPresets.map((preset) => (
                          <Button
                            key={preset.name}
                            size="small"
                            variant="outlined"
                            onClick={() => handlePresetClick(preset)}
                            disabled={loading}
                          >
                            {preset.name}
                          </Button>
                        ))}
                      </Box>
                    )}
                    
                    <FormControl fullWidth>
                      <InputLabel>Country{multiCountryMode ? 'ies' : ''}</InputLabel>
                      <Select
                        multiple={multiCountryMode}
                        value={multiCountryMode ? selectedCountries : selectedCountries[0]}
                        onChange={(e: SelectChangeEvent<string | string[]>) => {
                          if (multiCountryMode) {
                            const value = e.target.value;
                            setSelectedCountries(typeof value === 'string' ? value.split(',') : value);
                          } else {
                            setSelectedCountries([e.target.value as string]);
                          }
                        }}
                        label={`Country${multiCountryMode ? 'ies' : ''}`}
                        disabled={loading}
                        input={multiCountryMode ? <OutlinedInput label="Countries" /> : undefined}
                        renderValue={multiCountryMode ? (selected) => {
                          const selectedArray = selected as string[];
                          return selectedArray.map(code => {
                            const country = countries.find(c => c.code === code);
                            return country ? `${country.flag} ${country.name}` : code;
                          }).join(', ');
                        } : undefined}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {multiCountryMode && (
                              <Checkbox checked={selectedCountries.indexOf(country.code) > -1} />
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{country.flag}</span>
                              <span>{country.name}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <FormControl fullWidth>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      label="Language"
                      disabled={loading}
                    >
                      {languages.map((language) => (
                        <MenuItem key={language.code} value={language.code}>
                          {language.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleBulkSearch}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  >
                    {loading ? 'Searching...' : 'Get Keyword Ideas'}
                  </Button>
                </Box>
              </TabPanel>

              {error && (
                <Box sx={{ p: 2 }}>
                  <Alert severity="error">{error}</Alert>
                </Box>
              )}
            </Card>

            {/* Filters (shown when results exist) */}
            {results.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Filters
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box>
                      <Typography gutterBottom>Min. Monthly Searches</Typography>
                      <Slider
                        value={minSearchVolume}
                        onChange={(e, value) => setMinSearchVolume(value as number)}
                        valueLabelDisplay="auto"
                        step={100}
                        marks
                        min={0}
                        max={10000}
                      />
                    </Box>

                    <FormControl fullWidth>
                      <InputLabel>Competition Level</InputLabel>
                      <Select
                        multiple
                        value={competitionFilter}
                        onChange={(e) => setCompetitionFilter(e.target.value as string[])}
                        label="Competition Level"
                        renderValue={(selected) => selected.join(', ')}
                      >
                        {['LOW', 'MEDIUM', 'HIGH'].map((level) => (
                          <MenuItem key={level} value={level}>
                            {level}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={showOnlyQuestions}
                          onChange={(e) => setShowOnlyQuestions(e.target.checked)}
                        />
                      }
                      label="Show only question keywords"
                    />
                  </Box>
                </Box>
              </Card>
            )}
          </Grid>

          {/* Results Section - Always visible with table structure */}
          <Grid item xs={12} lg={8} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Keyword Ideas {results.length > 0 && `(${filteredResults.length} results)`}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, overflow: 'hidden', px: 3, pb: 3 }}>
                <TableContainer 
                  component={Paper} 
                  variant="outlined" 
                  sx={{ 
                    height: '100%',
                    maxHeight: 'calc(100vh - 350px)',
                    overflow: 'auto'
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedKeywords.size > 0 && selectedKeywords.size < sortedResults.length}
                            checked={sortedResults.length > 0 && selectedKeywords.size === sortedResults.length}
                            onChange={handleSelectAll}
                            disabled={sortedResults.length === 0}
                          />
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'keyword'}
                            direction={orderBy === 'keyword' ? order : 'asc'}
                            onClick={() => handleRequestSort('keyword')}
                            sx={sortLabelStyle}
                          >
                            Keyword
                          </TableSortLabel>
                        </TableCell>
                        {multiCountryMode && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'country'}
                              direction={orderBy === 'country' ? order : 'asc'}
                              onClick={() => handleRequestSort('country')}
                              sx={sortLabelStyle}
                            >
                              Country
                            </TableSortLabel>
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <TableSortLabel
                              active={orderBy === 'avgMonthlySearches'}
                              direction={orderBy === 'avgMonthlySearches' ? order : 'asc'}
                              onClick={() => handleRequestSort('avgMonthlySearches')}
                              sx={sortLabelStyle}
                            >
                              Monthly Searches
                            </TableSortLabel>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <TableSortLabel
                            active={orderBy === 'competition'}
                            direction={orderBy === 'competition' ? order : 'asc'}
                            onClick={() => handleRequestSort('competition')}
                            sx={sortLabelStyle}
                          >
                            Competition
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <TableSortLabel
                              active={orderBy === 'lowTopPageBid'}
                              direction={orderBy === 'lowTopPageBid' ? order : 'asc'}
                              onClick={() => handleRequestSort('lowTopPageBid')}
                              sx={sortLabelStyle}
                            >
                              Low Bid
                            </TableSortLabel>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <TableSortLabel
                              active={orderBy === 'highTopPageBid'}
                              direction={orderBy === 'highTopPageBid' ? order : 'asc'}
                              onClick={() => handleRequestSort('highTopPageBid')}
                              sx={sortLabelStyle}
                            >
                              High Bid
                            </TableSortLabel>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={multiCountryMode ? 7 : 6} align="center" sx={{ py: 8 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              Enter a keyword to see results
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Use the search form on the left to research keywords and get ideas
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : sortedResults.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={multiCountryMode ? 7 : 6} align="center" sx={{ py: 8 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              No keywords match your filters
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Try adjusting the filter settings to see more results
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedResults.map((result, index) => (
                          <TableRow 
                            key={index} 
                            hover
                            selected={selectedKeywords.has(result.keyword)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedKeywords.has(result.keyword)}
                                onChange={() => handleKeywordSelection(result.keyword)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>{result.keyword}</Typography>
                                <Tooltip title="Copy keyword">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopyKeyword(result.keyword)}
                                    sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            {multiCountryMode && (
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>{countries.find(c => c.code === result.countryCode)?.flag || ''}</span>
                                  <Typography variant="body2">
                                    {result.country || result.countryCode || ''}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )}
                            <TableCell align="right">
                              {result.avgMonthlySearches.toLocaleString()}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={result.competition}
                                color={getCompetitionColor(result.competition) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">${result.lowTopPageBid.toFixed(2)}</TableCell>
                            <TableCell align="right">${result.highTopPageBid.toFixed(2)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              
              {/* Keyword Tracking Section */}
              {selectedKeywords.size > 0 && (
                <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>
                    Save Selected Keywords ({selectedKeywords.size} selected)
                  </Typography>
                  
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Select a website and URL to save these keywords as secondary keywords.
                      </Alert>
                    </Grid>
                    
                    {/* Website Selection */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth disabled={loadingWebsites}>
                        <InputLabel>Target Website *</InputLabel>
                        <Select
                          value={selectedTargetWebsite?.website_id || ''}
                          onChange={(e) => {
                            const website = availableWebsites.find(w => w.website_id === e.target.value);
                            setSelectedTargetWebsite(website || null);
                          }}
                          label="Target Website *"
                          required
                        >
                          <MenuItem value="">
                            <em>Select a website</em>
                          </MenuItem>
                          {availableWebsites.map((website) => (
                            <MenuItem key={website.website_id} value={website.website_id}>
                              {formatWebsiteDisplay(website.base_url)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {/* URL Selection */}
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        disabled={!selectedTargetWebsite || loadingUrls}
                        value={selectedTargetUrl}
                        onChange={(event, newValue) => {
                          setSelectedTargetUrl(newValue);
                        }}
                        options={availableUrls}
                        getOptionLabel={(option) => option.full_url}
                        isOptionEqualToValue={(option, value) => option.url_id === value?.url_id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Target URL *"
                            placeholder="Type to search URLs..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingUrls ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} sx={{ fontSize: '0.875rem' }}>
                            <Box>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 500 }}>
                                {option.full_url}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.url_id}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        sx={{ minHeight: 56 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        All keywords will be saved as secondary keywords
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSaveSelectedKeywords}
                        disabled={savingKeywords || !selectedTargetWebsite || !selectedTargetUrl}
                        startIcon={savingKeywords ? <CircularProgress size={20} /> : <SaveIcon />}
                      >
                        {savingKeywords ? 'Saving Keywords...' : 'Save Selected Keywords'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
        ) : (
          // NEW RELATED + RANKINGS ANALYSIS FUNCTIONALITY
          <Grid container spacing={3} sx={{ height: 'calc(100vh - 200px)' }}>
            {/* Analysis Input Section */}
            <Grid item xs={12} lg={4}>
              <Card sx={{ height: 'fit-content' }}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon sx={{ color: '#7b61ff' }} />
                    Analyze Keyword Universe
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Get related keywords, current rankings, and content opportunities
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Enter keyword to analyze"
                    value={baseKeywordForAnalysis}
                    onChange={(e) => setBaseKeywordForAnalysis(e.target.value)}
                    sx={{ mb: 2 }}
                    placeholder="e.g., new york attractions"
                    disabled={analysisLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Website for Rankings Analysis</InputLabel>
                    <Select
                      value={selectedWebsiteForRankings}
                      onChange={(e) => setSelectedWebsiteForRankings(e.target.value)}
                      label="Website for Rankings Analysis"
                      disabled={analysisLoading || loadingWebsites}
                    >
                      <MenuItem value="">
                        <em>Select a website</em>
                      </MenuItem>
                      {availableWebsites.map(website => (
                        <MenuItem key={website.website_id} value={website.website_id}>
                          {formatWebsiteDisplay(website.base_url)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAnalyzeKeywordUniverse}
                    disabled={analysisLoading || !baseKeywordForAnalysis.trim() || !selectedWebsiteForRankings}
                    startIcon={analysisLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                    sx={{ 
                      bgcolor: '#7b61ff',
                      '&:hover': { bgcolor: '#6b51e0' },
                      py: 1.5
                    }}
                  >
                    {analysisLoading ? 'Analyzing...' : 'Analyze Keyword Universe'}
                  </Button>
                  
                  {analysisLoading && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress sx={{ borderRadius: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Searching related keywords and analyzing opportunities...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>

              {/* Analysis Info Card */}
              {(relatedKeywordsData.length > 0 || paaQuestionsData.length > 0) && (
                <Card sx={{ mt: 3 }}>
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Analysis Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b61ff' }}>
                            {relatedKeywordsData.length}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Related Keywords
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF9800' }}>
                            {paaQuestionsData.length}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            PAA Questions
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Card>
              )}
            </Grid>
            
            {/* Analysis Results Section */}
            <Grid item xs={12} lg={8} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {relatedKeywordsData.length === 0 && paaQuestionsData.length === 0 ? (
                  <Box sx={{ 
                    p: 6, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    textAlign: 'center'
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
                    <Typography variant="h5" color="text.secondary" gutterBottom>
                      Ready to Analyze Keywords
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                      Enter a keyword and select a website to discover related keywords, current rankings, and content opportunities.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ p: 3, pb: 0 }}>
                      <Tabs 
                        value={analysisResultsTab} 
                        onChange={(e, newValue) => setAnalysisResultsTab(newValue)}
                        sx={{
                          '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <Tab 
                          icon={<TrendingUpIcon />} 
                          iconPosition="start"
                          label={`Related Keywords (${relatedKeywordsData.length})`} 
                        />
                        <Tab 
                          icon={<QuestionAnswerIcon />}
                          iconPosition="start" 
                          label={`People Also Ask (${paaQuestionsData.length})`} 
                        />
                        <Tab 
                          icon={<InsightsIcon />}
                          iconPosition="start" 
                          label={`Opportunities (${opportunitiesData.length})`} 
                        />
                      </Tabs>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'hidden', p: 3, pt: 2 }}>
                      {/* Related Keywords Tab */}
                      {analysisResultsTab === 0 && (
                        <RelatedKeywordsTable data={relatedKeywordsData} />
                      )}
                      
                      {/* PAA Questions Tab */}
                      {analysisResultsTab === 1 && (
                        <PAAQuestionsCards data={paaQuestionsData} />
                      )}
                      
                      {/* Opportunities Tab */}
                      {analysisResultsTab === 2 && (
                        <OpportunitiesCards data={opportunitiesData} />
                      )}
                    </Box>
                  </>
                )}
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar({ open: false, message: '' })}
        message={copySnackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default KeywordResearchPage;